/**
 * Metrics § Follower growth (§8.9, PRD Metrics §3; redesign § Follower growth).
 *
 * Snapshot points plotted as-is: a profile view gets one color-filled area, a group one thin line
 * per member in distinct muted hues with a small legend and no fill (overlapping fills unreadable;
 * no client summing — §12.5). Group series are aligned on the server's `capturedAtDisplay` strings
 * (scrape-day identity), which is layout, not arithmetic. Every point is "as of scrape":
 * granularity equals scrape frequency, which the section tooltip states. Deltas arrive
 * pre-computed and pre-formatted (redesign server-side exception 1); the raw values pick the sign
 * class only.
 *
 * The stat row pairs the audience size with the five §8.9 engagement rates (Σ metric ÷ latest
 * followers × 100) — every percentage arrives formatted, tooltip included; the labels are the
 * only text this file owns.
 */

import type { ReactNode } from 'react';
import type { FollowerGrowthData, FollowerPointDTO, FollowerSeriesDTO } from '@lw/shared';
import { Chart, ChartLegend, StatCard, type ChartSeries } from '../../../components';
import { formatInteger } from '../../../lib/format';

/** Group member hues — the six series tokens, cycled (redesign [D]). */
const SERIES_COLORS = [
  'var(--series-posts)',
  'var(--series-reactions)',
  'var(--series-comments)',
  'var(--series-shares)',
  'var(--series-saves)',
  'var(--series-total)',
] as const;

function signClass(value: number | null): 'up' | 'down' | 'flat' {
  if (value === null || value === 0) return 'flat';
  return value > 0 ? 'up' : 'down';
}

/** Change / MoM % rows — server display strings verbatim, colored by the raw delta's sign. */
function DeltaRows({ point }: { point: FollowerPointDTO }) {
  if (point.changeDisplay === null && point.changePctDisplay === null) return null;
  return (
    <div className="t-deltas">
      {point.changeDisplay !== null ? (
        <div className="t-row">
          <span className="t-label">Change</span>
          <span className={`t-delta ${signClass(point.change)}`}>{point.changeDisplay}</span>
        </div>
      ) : null}
      {point.changePctDisplay !== null ? (
        <div className="t-row">
          <span className="t-label">MoM %</span>
          <span className={`t-delta ${signClass(point.changePct)}`}>{point.changePctDisplay}</span>
        </div>
      ) : null}
    </div>
  );
}

function SingleFollowerChart({ series }: { series: FollowerSeriesDTO }) {
  const points = series.points;
  const chartSeries: ChartSeries[] = [
    {
      key: 'followers',
      type: 'line',
      color: 'var(--series-reactions)',
      values: points.map((point) => point.followers),
      area: true,
    },
  ];

  const renderTooltip = (index: number): ReactNode => {
    const point = points[index];
    if (point === undefined || point.followers === null) return null;
    return (
      <div>
        <div className="t-label">{point.capturedAtDisplay}</div>
        <div className="t-row">
          <span className="t-label">Followers</span>
          {/* Separators only: presenting a raw chart value is rendering, not client math (§12.5). */}
          <span className="t-value">{formatInteger(point.followers)}</span>
        </div>
        <DeltaRows point={point} />
      </div>
    );
  };

  return (
    <Chart
      labels={points.map((point) => point.capturedAtDisplay)}
      series={chartSeries}
      height={260}
      renderTooltip={renderTooltip}
      ariaLabel="Follower count per scrape"
    />
  );
}

function GroupFollowerChart({ series }: { series: FollowerSeriesDTO[] }) {
  // Align members on shared scrape-day buckets keyed by the server's display string; a member
  // without a point in a bucket plots a gap. Ordered by the earliest capture in each bucket.
  const bucketStart = new Map<string, number>();
  for (const member of series) {
    for (const point of member.points) {
      const current = bucketStart.get(point.capturedAtDisplay);
      if (current === undefined || point.capturedAt < current) {
        bucketStart.set(point.capturedAtDisplay, point.capturedAt);
      }
    }
  }
  const buckets = [...bucketStart.entries()].sort((a, b) => a[1] - b[1]).map(([label]) => label);
  const bucketIndex = new Map(buckets.map((label, index) => [label, index]));

  const members = series.map((member, index) => {
    const points: (FollowerPointDTO | undefined)[] = buckets.map(() => undefined);
    for (const point of member.points) {
      const slot = bucketIndex.get(point.capturedAtDisplay);
      if (slot !== undefined) points[slot] = point;
    }
    return { member, color: SERIES_COLORS[index % SERIES_COLORS.length], points };
  });

  const chartSeries: ChartSeries[] = members.map(({ member, color, points }) => ({
    key: String(member.memberId),
    type: 'line',
    color,
    values: points.map((point) => (point === undefined ? null : point.followers)),
  }));

  const renderTooltip = (index: number): ReactNode => {
    const label = buckets[index];
    const hasAny = members.some(({ points }) => {
      const point = points[index];
      return point !== undefined && point.followers !== null;
    });
    if (label === undefined || !hasAny) return null;
    return (
      <div>
        <div className="t-label">{label}</div>
        {members.map(({ member, color, points }) => {
          const point = points[index];
          if (point === undefined || point.followers === null) return null;
          return (
            <div key={member.memberId}>
              <div className="t-row">
                <span className="t-label">
                  <span className="t-dot" style={{ background: color }} aria-hidden />
                  {member.memberName}
                </span>
                <span className="t-value">{formatInteger(point.followers)}</span>
              </div>
              <DeltaRows point={point} />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Chart
        labels={buckets}
        series={chartSeries}
        height={260}
        renderTooltip={renderTooltip}
        ariaLabel="Follower count per member per scrape"
      />
      <ChartLegend items={members.map(({ member, color }) => ({ label: member.memberName, color }))} />
    </>
  );
}

export function FollowerGrowthSection({ data }: { data: FollowerGrowthData }) {
  const single = data.series.length === 1 ? data.series[0] : undefined;

  return (
    <div className="card">
      <div className="card__body">
        <div className="fg-stats">
          <StatCard label="Total Followers" value={data.latest} />
          <StatCard label="Reaction rate" value={data.rates.reactions} />
          <StatCard label="Comment rate" value={data.rates.comments} />
          <StatCard label="Share rate" value={data.rates.shares} />
          <StatCard label="Save rate" value={data.rates.saves} />
          <StatCard label="Engagement rate (total)" value={data.rates.totalEngagement} />
        </div>

        <div className="fg-chart">
          {single !== undefined ? (
            <SingleFollowerChart series={single} />
          ) : (
            <GroupFollowerChart series={data.series} />
          )}
        </div>
      </div>
    </div>
  );
}

export default FollowerGrowthSection;
